import { type FormEvent, useEffect, useState } from "react";
import { AdminPageHeader } from "../../components/admin/AdminPageHeader";
import { AdminPanel } from "../../components/admin/AdminPanel";
import { FeatureDisabledState } from "../../components/trainer/FeatureDisabledState";
import { Button } from "../../components/ui/Button";
import { EmptyState } from "../../components/ui/EmptyState";
import { FullPageLoader } from "../../components/ui/FullPageLoader";
import { Input } from "../../components/ui/Input";
import { useTenant } from "../../hooks/useTenant";
import { useTrainerProfile } from "../../hooks/useTrainerProfile";
import { useWorkspaceFeatures } from "../../hooks/useWorkspaceFeatures";
import { trainerProfileService } from "../../services/trainerProfileService";

export function TrainerProfilePage() {
  const { currentTenant } = useTenant();
  const { data: features } = useWorkspaceFeatures(currentTenant?.id);
  const { data, loading, error, refresh } = useTrainerProfile(currentTenant?.id);
  const [form, setForm] = useState({
    display_name: "",
    role_title: "",
    bio: "",
    qualifications: "",
    specialisms: "",
    contact_email: "",
    contact_phone: "",
  });
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    if (!data) return;
    setForm({
      display_name: data.display_name,
      role_title: data.role_title,
      bio: data.bio,
      qualifications: data.qualifications ?? "",
      specialisms: data.specialisms.join(", "),
      contact_email: data.contact_email,
      contact_phone: data.contact_phone ?? "",
    });
  }, [data]);

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!data) return;
    setSaving(true);
    setActionError(null);
    try {
      await trainerProfileService.update(data.id, {
        display_name: form.display_name,
        role_title: form.role_title,
        bio: form.bio,
        qualifications: form.qualifications || null,
        specialisms: form.specialisms.split(",").map((item) => item.trim()).filter(Boolean),
        contact_email: form.contact_email,
        contact_phone: form.contact_phone || null,
      });
      await refresh();
    } catch (caughtError) {
      setActionError(caughtError instanceof Error ? caughtError.message : "Unable to save trainer profile.");
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <FullPageLoader label="Loading trainer profile" />;
  if (error) return <EmptyState title="Unable to load trainer profile.">{error}</EmptyState>;
  if (features && !features.trainer_profiles_enabled) {
    return <FeatureDisabledState title="Trainer profiles are not enabled." />;
  }

  return (
    <div>
      <AdminPageHeader title="Trainer Profile" description="Your professional profile inside this workspace." />
      <AdminPanel title="Profile details">
        {actionError ? <div className="mb-5 rounded-md border border-red-900/20 bg-red-50 p-4 text-sm text-red-900">{actionError}</div> : null}
        <form className="grid gap-4 lg:grid-cols-2" onSubmit={save}>
          <Input label="Display name" value={form.display_name} onChange={(event) => setForm({ ...form, display_name: event.target.value })} required />
          <Input label="Role title" value={form.role_title} onChange={(event) => setForm({ ...form, role_title: event.target.value })} />
          <Input label="Contact email" type="email" value={form.contact_email} onChange={(event) => setForm({ ...form, contact_email: event.target.value })} />
          <Input label="Contact phone" value={form.contact_phone} onChange={(event) => setForm({ ...form, contact_phone: event.target.value })} />
          <Input label="Qualifications" value={form.qualifications} onChange={(event) => setForm({ ...form, qualifications: event.target.value })} />
          <Input label="Specialisms" value={form.specialisms} onChange={(event) => setForm({ ...form, specialisms: event.target.value })} placeholder="Strength, fat loss, rehab" />
          <label className="block text-sm font-medium text-ink lg:col-span-2">
            Bio
            <textarea className="mt-2 min-h-32 w-full rounded-md border border-line bg-field px-3 py-2" value={form.bio} onChange={(event) => setForm({ ...form, bio: event.target.value })} />
          </label>
          <Button className="lg:col-span-2" isLoading={saving} type="submit">Save trainer profile</Button>
        </form>
      </AdminPanel>
    </div>
  );
}
